/**
 * Upload Processor
 * Handles automatic upload retry and network monitoring
 */

import uploadQueue from './uploadQueue';
import { uploadBase64, updateDocument, getFileURL } from './db_calls';
import { readAndCompressImage } from 'browser-image-resizer';
import { increment } from 'firebase/firestore';

class UploadProcessor {
  constructor() {
    this.isProcessing = false;
    this.isOnline = navigator.onLine;
    this.retryInterval = null;
    this.listeners = [];
  }

  /**
   * Initialize processor and network monitoring
   */
  init() {
    console.log('🚀 Upload processor initialized');

    // Monitor network status
    window.addEventListener('online', () => {
      console.log('🌐 Network restored - processing upload queue');
      this.isOnline = true;
      this.notifyListeners({ type: 'network', online: true });
      this.processQueue();
    });

    window.addEventListener('offline', () => {
      console.log('📴 Network lost');
      this.isOnline = false;
      this.notifyListeners({ type: 'network', online: false });
    });

    // Start periodic queue check (every 30 seconds)
    this.retryInterval = setInterval(() => {
      if (this.isOnline) {
        this.processQueue();
      }
    }, 30000);

    // Process any existing queue items
    if (this.isOnline) {
      setTimeout(() => this.processQueue(), 1000);
    }
  }

  /**
   * Stop processor
   */
  stop() {
    if (this.retryInterval) {
      clearInterval(this.retryInterval);
      this.retryInterval = null;
    }
  }

  /**
   * Process all queued uploads
   */
  async processQueue() {
    if (this.isProcessing) {
      console.log('⏳ Upload processing already in progress');
      return;
    }

    if (!this.isOnline) {
      console.log('📴 Offline - skipping queue processing');
      return;
    }

    this.isProcessing = true;

    try {
      const queuedItems = await uploadQueue.getQueuedUploads();
      
      if (queuedItems.length === 0) {
        console.log('✅ Upload queue is empty');
        this.isProcessing = false;
        return;
      }

      console.log(`📤 Processing ${queuedItems.length} queued upload(s)`);

      for (const item of queuedItems) {
        await this.processUpload(item);
      }

      console.log('✅ Finished processing upload queue');
    } catch (error) {
      console.error('❌ Error processing upload queue:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Process a single upload
   */
  async processUpload(item) {
    const { id, photoData, customerPhone, photoId, retryCount } = item;

    console.log(`🔍 Processing upload for photo ${photoId}:`, { id, customerPhone, photoId, retryCount });

    // Skip if too many retries
    if (retryCount >= 5) {
      console.log(`⚠️ Max retries reached for photo ${photoId}, marking as failed`);
      await uploadQueue.updateStatus(id, 'failed', 'Max retries exceeded');
      this.notifyListeners({ 
        type: 'upload_failed', 
        photoId, 
        queueId: id,
        error: 'Max retries exceeded'
      });
      return;
    }

    try {
      console.log(`📤 Uploading photo ${photoId} (Queue ID: ${id}, Attempt: ${retryCount + 1})`);
      
      // Update status to uploading
      await uploadQueue.updateStatus(id, 'uploading');
      this.notifyListeners({ 
        type: 'upload_started', 
        photoId, 
        queueId: id 
      });

      // Convert base64 to blob for thumbnail creation
      const response = await fetch(photoData);
      const blob = await response.blob();
      const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });

      // Create thumbnail
      const thumbnailConfig = {
        quality: 0.7,
        maxWidth: 300,
        maxHeight: 300,
        autoRotate: true,
        mimeType: 'image/jpeg'
      };

      const thumbnailBlob = await readAndCompressImage(file, thumbnailConfig);

      // Convert thumbnail to base64
      const thumbnailBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(thumbnailBlob);
      });

      const timestamp = Date.now();

      // Upload full-size photo
      const fullSizePath = `customers/${customerPhone}/photos/full_${timestamp}.jpg`;
      await uploadBase64(fullSizePath, photoData, 'data_url', {
        contentType: 'image/jpeg',
        customMetadata: {
          customerPhone: customerPhone,
          type: 'full',
          uploadedAt: new Date().toISOString(),
          queueId: String(id)
        }
      });

      // Upload thumbnail
      const thumbnailPath = `customers/${customerPhone}/photos/thumb_${timestamp}.jpg`;
      await uploadBase64(thumbnailPath, thumbnailBase64, 'data_url', {
        contentType: 'image/jpeg',
        customMetadata: {
          customerPhone: customerPhone,
          type: 'thumbnail',
          uploadedAt: new Date().toISOString(),
          queueId: String(id)
        }
      });

      // Mark as uploaded
      await uploadQueue.updateStatus(id, 'uploaded');
      
      // Increment customer photo counter in Firestore
      try {
        await updateDocument('customers', customerPhone, {
          photoCount: increment(1)
        }, false); // Don't add updatedAt when incrementing
        
        console.log(`📊 Incremented photo counter for customer ${customerPhone}`);
      } catch (error) {
        console.error(`⚠️ Failed to increment photo counter:`, error);
        // Don't fail the upload if counter update fails
      }
      
      console.log(`✅ Successfully uploaded photo ${photoId} (Queue ID: ${id})`);
      
      // Get download URL for the full-size photo (for MMS sending)
      let downloadUrl = null;
      try {
        downloadUrl = await getFileURL(fullSizePath);
        console.log(`🔗 Download URL obtained: ${downloadUrl}`);
      } catch (error) {
        console.warn(`⚠️ Failed to get download URL:`, error);
      }
      
      this.notifyListeners({ 
        type: 'upload_success', 
        photoId, 
        queueId: id,
        fullSizePath,
        thumbnailPath,
        downloadUrl
      });

      // Remove from queue immediately after successful upload to free memory
      await uploadQueue.removeFromQueue(id);
      console.log(`🗑️ Removed photo ${photoId} from IndexedDB queue`);

    } catch (error) {
      console.error(`❌ Failed to upload photo ${photoId}:`, error);
      
      // Check if it's a network error
      const isNetworkError = 
        error.message.includes('network') || 
        error.message.includes('fetch') ||
        error.code === 'storage/unknown' ||
        !navigator.onLine;

      if (isNetworkError) {
        // Network error - requeue
        await uploadQueue.updateStatus(id, 'queued');
        console.log(`🔄 Requeued photo ${photoId} due to network error`);
        this.notifyListeners({ 
          type: 'upload_requeued', 
          photoId, 
          queueId: id,
          error: error.message
        });
      } else {
        // Other error - mark as failed
        await uploadQueue.updateStatus(id, 'failed', error.message);
        console.log(`⚠️ Marked photo ${photoId} as failed`);
        this.notifyListeners({ 
          type: 'upload_failed', 
          photoId, 
          queueId: id,
          error: error.message
        });
      }
    }
  }

  /**
   * Add listener for upload events
   */
  addListener(callback) {
    this.listeners.push(callback);
  }

  /**
   * Remove listener
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(l => l !== callback);
  }

  /**
   * Notify all listeners
   */
  notifyListeners(event) {
    this.listeners.forEach(callback => callback(event));
  }

  /**
   * Get current status
   */
  getStatus() {
    return {
      isOnline: this.isOnline,
      isProcessing: this.isProcessing
    };
  }
}

// Create singleton instance
const uploadProcessor = new UploadProcessor();

export default uploadProcessor;

